import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { TemporaryMedia } from '../models/email-template/temporary-media.interface';
import { TypeEnum } from '../models/template/type.enum';

@Injectable({
  providedIn: 'root',
})
export class GlobalTemplateService {
  private templateId: string = ''; // Initialize your templateId value
  private isDisabledSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(true); // BehaviorSubject is ideal for tracking the current state
  private isNewTemplate: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false); // BehaviorSubject is ideal for tracking the current state

  private templateNameSubject: BehaviorSubject<string> =
    new BehaviorSubject<string>('');
  templateNameData$ = this.templateNameSubject.asObservable();

  private templateIdSubject: BehaviorSubject<string> =
    new BehaviorSubject<string>('');
  templateIdData$ = this.templateIdSubject.asObservable();

  private isNewTemplateSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  isNewTemplateData$ = this.isNewTemplateSubject.asObservable();

  private temporaryMediaSubject: BehaviorSubject<TemporaryMedia[]> =
    new BehaviorSubject<TemporaryMedia[]>([]);
  temporaryMediaData$ = this.temporaryMediaSubject.asObservable();

  private mediaForDeletionWithinABlockSubject: BehaviorSubject<string[]> =
    new BehaviorSubject<string[]>([]);
  mediaForDeletionWithinABlock$ =
    this.mediaForDeletionWithinABlockSubject.asObservable();

  private mediaForDeletionSubject: BehaviorSubject<string[]> =
    new BehaviorSubject<string[]>([]);
  mediaForDeletion$ = this.mediaForDeletionSubject.asObservable();

  private cachedTemplateSubject: BehaviorSubject<any> =
    new BehaviorSubject<any>(null);
  cachedTemplateData$ = this.cachedTemplateSubject.asObservable();

  private templateTypeSubject: BehaviorSubject<any> =
    new BehaviorSubject<any>(null);
  templateTypeData$ = this.templateTypeSubject.asObservable();

  constructor() {
  }

  cacheTranslateTemplateData(templateData: any) {
    this.cachedTemplateSubject.next(templateData);
  }

  setTemplateTypeData(templateType: TypeEnum) {
    this.templateTypeSubject.next(templateType);
  }

  /**
   * Clears the lists of media marked for deletion by emitting empty arrays
   * to both `mediaForDeletionSubject` and `mediaForDeletionWithinABlockSubject`.
   *
   */
  clearMediaForDeletion() {
    this.mediaForDeletionSubject.next([]);
    this.mediaForDeletionWithinABlockSubject.next([]);
  }

  /**
   * Replaces both lists of media marked for deletion, e.g. when the v2 editor
   * restores an undo/redo snapshot. The arrays are copied so later appends
   * can't change the caller's arrays.
   */
  restoreMediaForDeletion(mediaURIs: string[], blockIds: string[]): void {
    this.mediaForDeletionSubject.next([...mediaURIs]);
    this.mediaForDeletionWithinABlockSubject.next([...blockIds]);
  }

  setMediaForDeletionWithinABlockData(blockId: string): void {
    const currentMediaForDeletion =
      this.mediaForDeletionWithinABlockSubject.getValue();

    //Check for duplicates
    const foundBlockId = currentMediaForDeletion.find(
      (existingBlockId) => existingBlockId === blockId
    );

    // If no duplicate was found, save it
    if (!foundBlockId) {
      this.mediaForDeletionWithinABlockSubject.next([
        ...currentMediaForDeletion,
        blockId,
      ]);
    }
  }

  setMediaForDeletionData(mediaURI: string): void {
    const currentMediaForDeletion = this.mediaForDeletionSubject.getValue();

    // Check for duplicates
    const foundMediaUri = currentMediaForDeletion.find(
      (mediaURIElement) => mediaURIElement === mediaURI
    );

    if (!foundMediaUri) {
      this.mediaForDeletionSubject.next([
        ...currentMediaForDeletion,
        mediaURI,
      ]);
    }
  }

  setTemporaryMediaData(media: TemporaryMedia): void {
    const currentMedia = this.temporaryMediaSubject.getValue();
    this.temporaryMediaSubject.next([...currentMedia, media]);
  }

  setNewTemporaryMediaData(newMediaArray: TemporaryMedia[]): void {
    this.temporaryMediaSubject.next([...newMediaArray]);
  }

  setIsNewTemplateData(isNew: boolean): void {
    this.isNewTemplateSubject.next(isNew);
  }

  setTemplateNameData(name: string): void {
    this.templateNameSubject.next(name);
  }

  setTemplateIdData(id: string): void {
    this.templateIdSubject.next(id);
  }

  // Getter to retrieve the templateId
  getTemplateId(): string {
    return this.templateId;
  }

  // Setter to update the templateId
  setTemplateId(id: string): void {
    this.templateId = id;
  }

  // Observable getter for isDisabled
  getIsDisabled() {
    return this.isDisabledSubject.asObservable();
  }

  // Setter to update isDisabled
  setIsDisabled(state: boolean) {
    this.isDisabledSubject.next(state);
  }

  // Observable getter for isDisabled
  getIsNew() {
    return this.isNewTemplate.asObservable();
  }

  // Setter to update isDisabled
  setIsNew(state: boolean) {
    this.isNewTemplate.next(state);
  }
}
